

import React, { Fragment, useState, useContext } from 'react';


// Utils

import { processAsJson } from '@utils/miscellaneous/processAsJson';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Context

import { FullDataContext } from '@contexts/documentsVerificationContexts';
import { getContext } from '@contexts/documentsVerificationContexts';

// Constants

import { checkIfConfigFails, getVerificationDocumentsConfig } from '@utils/miscellaneous/getVerificationDocumentsConfig';
import { documentsVerificationConfig } from '@constants/configs/QLEDocumentVerificationConfigs/documentVerificationConfig';
import { UPLOADED_DOCUMENT_STATUSES } from '@constants/common/miscellaneous';
import { omit } from '@utils/miscellaneous/objectHelpers';





const UploadHyperDashboard = props => {

    const {
        allDocumentsAreProvided,
        applicantInfo,
        applicantInfo: {
            applicantGuid: currentApplicantGuid,
            event
        },
        isExtended,
        isSuperUser,
        parentActions: {
            updateMockData,
            updateEnvironmentProp
        },
        stateCode
    } = props;

    const fullData = useContext(FullDataContext);

    const [fileInfo] = useContext(getContext('fileInfo')) as any;

    const [ backEndDataView, switchBackEndData ] = useState('Full Data');

    const [ uiConfigDataView, switchUiConfigData ] = useState('Config for Event');

    const [
        views,
        toggleViews
    ] = useState({
        isUiConfigViewShown: false,
        isUploadViewShown: false,
        isBackEndDataShown: false,
        isJsonInjectorShown: false,
        isProcessingShown: true
    });

    const {
        isUiConfigViewShown,
        isUploadViewShown,
        isBackEndDataShown,
        isJsonInjectorShown,
        isProcessingShown
    } = views;

    const [ json, updateJson ] = useState('');

    const inlineStyles = {
        commonForSections: {
            fontSize: '1.2rem',
            width: '600px',
            padding: '0.5rem'
        },
        uploadSection: {
            height: '200px',
            border: '1px solid yellow'
        },
        uiConfigSection: {
            height: '30%',
            border: '1px solid red'
        },
        backEndDataSection: {
            height: '30%',
            border: '1px solid green'
        },
        button: {
            display: 'inline-block',
            fontSize: '1.3rem',
            padding: '1px 8px',
            borderRadius: '3px',
            marginRight: '15px',
        },
        extraWide: {
            width: '600px'
        },
        label: {
            fontSize: '1.2rem',
            fontWeight: 'bold'
        },
        spaceBetweenItems: {
            marginBottom: '1rem'
        },
        documentRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '0.5rem 0',
            borderBottom: '1px solid #eee'
        },
        documentMeta: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '0.15rem',
            minWidth: 0
        },
        documentControls: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexShrink: 0
        },
        controlLabel: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '1.1rem',
            fontWeight: 'normal',
            whiteSpace: 'nowrap' as const,
            cursor: 'pointer'
        },
        controlCheckbox: {
            margin: 0,
            width: '1rem',
            height: '1rem',
            cursor: 'inherit'
        },
        statusBadge: {
            fontSize: '0.95rem',
            color: 'grey'
        }
    };

    const transformUiConfig = (config, stateCode) => {
        return [ ...config[stateCode]];
    };

    const stringify = payload => JSON.stringify(payload, null, 2);

    const handleJsonChange = e => {
        const {
            target: {
                value
            }
        } = e;

        updateJson(value);
    };

    const getDataButtons = () => getButtons(
        [
            'Applicant Data',
            'Grouped Data',
            'Full Data'
        ],
        backEndDataView,
        switchBackEndData,
        views.isBackEndDataShown
    );
    
    const getUiConfigButtons = () => getButtons(
        [
            'Config for State',
            'Config for Event'
        ],
        uiConfigDataView,
        switchUiConfigData,
        views.isUiConfigViewShown
    );

    const getUserSwitchButtons = () => getButtons(
        [
            'SuperUser',
            'Customer'
        ],
        isSuperUser ? 'SuperUser' : 'Customer',
        value => {
            const nextIsSuperUser = value === 'SuperUser';
            updateEnvironmentProp('user.role', nextIsSuperUser ? 'ADMIN' : 'INDIVIDUAL');
            // Recompute `deletable` on every document for the new role - see
            // useDocumentVerificationMockDataUpdate.js's 'recomputeDeletable' case. Without this,
            // documents already processed under the previous role would keep a stale `deletable`
            // value and the Edit icon wouldn't reflect the role you just switched to.
            updateMockData('recomputeDeletable', { isSuperUser: nextIsSuperUser });
        },
        true
    );

    const getButtons = (buttons, isShown, onClickAction, isSectionShown) => {
        return buttons.map(button => (
            <button
                style = {{
                    ...inlineStyles.button,
                    ...(
                        isSectionShown ?
                            {
                                backgroundColor: button === isShown ? 'green' : 'initial',
                                color: button === isShown ? 'white' : 'initial'
                            } :
                            {}
                    ),
                }}
                key = { button }
                name = { button }
                // @ts-ignore
                onClick = { ({ target: { name } }) => { onClickAction(name); }}
            >
                { button }
            </button>
        ));
    };
    
    const getUiConfigDataView = () => {

        switch(uiConfigDataView) {
            case 'Config for State':
                return (
                    <Fragment>
                        <b>State: { stateCode }</b>
                        <br/>
                        <br/>
                        { stringify(transformUiConfig(documentsVerificationConfig, stateCode)) }
                    </Fragment>
                );

            case 'Config for Event':
                return (
                    <Fragment>
                        <b>Event: { event }</b>
                        <br/>
                        <br/>
                        { stringify(!checkIfConfigFails(stateCode, event) && getVerificationDocumentsConfig(stateCode, event)) }
                    </Fragment>
                );
        }
    };

    const getBackEndDataView = () => {

        const topLevelData = omit(fullData, [ 'applicants' ]);

        switch(backEndDataView) {
            case 'Applicant Data':
                return (
                    <Fragment>
                        Applicant data:
                        <br/>
                        { stringify(applicantInfo) }
                    </Fragment>
                );

            case 'Grouped Data':
                return (
                    <Fragment>
                        Meta data:
                        <br/>
                        { stringify({ ...topLevelData, ...omit(applicantInfo, [ 'documents' ]) }) }
                        <br/>
                        Documents Info:
                        <br/>
                        { stringify(applicantInfo.documents) }
                    </Fragment>
                );
            default:
                return (
                    <Fragment>
                        Full Data:
                        <br/>
                        { stringify(fullData) }
                    </Fragment>
                );
        }
    };

    const getSectionHeader = (label, view, buttons?) => {

        const isShown = views[view];

        return (
            <div
                className = 'grid-row'
                style = {{
                    ...inlineStyles.extraWide
                }}
            >
                <span
                    className = 'grid-col-3'
                    // @ts-ignore
                    style = {{
                        ...inlineStyles.label
                    }}
                >
                    { label }
                </span>
                {
                    view &&

                    <span className = 'grid-col-2'>
                        <button
                            style = {{
                                ...inlineStyles.button,
                                color: isShown ? 'red' : 'green'
                            }}
                            onClick = { () => toggleViews(views => ({ ...views, [ view ]: !isShown })) }
                        >
                            { isShown ? 'Hide' : 'Show' }
                        </button>
                    </span>
                }
                <span className = 'grid-col-7'>
                    { buttons }
                </span>
            </div>
        );
    };

    const handleJsonUpload = () => {

        const DEFAULT_STATE = {
            applicants: [],
            caseNumber: ''
        };

        const checkPayload = data => {
            if(data.payload) {
                if(data.payload.caseNumber) {
                    return data.payload;
                } else {
                    alert('JSON with =payload= property doesn\'t contain =caseNumber= property');
                    return fullData;
                }
            } else {
                if(data.caseNumber) {
                    return data;
                } else {
                    alert('JSON doesn\'t contain =caseNumber= property');
                    return fullData;
                }
            }
        };

        updateMockData('inject', checkPayload(processAsJson(json, DEFAULT_STATE)));
        updateJson('');
    };



    const getApprovalRejectionDashboard = () => {

        const handleSuperUserAction = e => {
            const payload = JSON.parse(e.target.dataset.info);
            updateMockData('processTicket', payload);
        };

        // `deletable` flag, as the backend would set it (only used here to compute a fresh value
        // when the Verified toggle changes status - see handleVerifiedToggle below): an INITIAL
        // document is always deletable (no associated ticket yet); any other status is deletable
        // only when the user has delete access. `isSuperUser` is the client-side proxy for that
        // delete-access role.
        const getDeletableFlag = documentStatus =>
            documentStatus === UPLOADED_DOCUMENT_STATUSES.INITIAL ? true : isSuperUser;

        const handleVerifiedToggle = e => {
            const info = JSON.parse(e.target.dataset.info);
            const verified = e.target.checked;
            const documentStatus = verified ?
                UPLOADED_DOCUMENT_STATUSES.VERIFIED :
                UPLOADED_DOCUMENT_STATUSES.SUBMITTED;

            updateMockData('injectVerifiedStatus', {
                ...info,
                verified,
                // Carry the same `deletable` flag the backend would set for this status/role.
                deletable: getDeletableFlag(documentStatus)
            });
        };

        const getVerifiedCheckbox = (info, documentStatus) => {
            return (
                <label style = {{ ...inlineStyles.controlLabel }}>
                    <input
                        type = 'checkbox'
                        style = {{ ...inlineStyles.controlCheckbox }}
                        checked = { documentStatus === UPLOADED_DOCUMENT_STATUSES.VERIFIED }
                        onChange = { handleVerifiedToggle }
                        data-info = { JSON.stringify(info) }
                    />
                    <span>Verified</span>
                </label>
            );
        };

        // Manual override for a document's `deletable` flag (drives DocumentInfo.shouldShowEditIcon,
        // i.e. whether the Edit icon shows in the main document list).
        //
        // This intentionally reflects the *raw* `deletable` value on the document, not a computed
        // "what it should be" default - a fallback here would make the checkbox show checked for
        // any INITIAL document even when the mock data was seeded without `deletable` at all (e.g.
        // via the JSON Injector, or a hand-written mock payload), while the main list's Edit icon
        // - which reads the same field directly - stays hidden. That mismatch is confusing: the
        // dashboard would claim "Editable" while nothing in the real UI reflects it. Showing the
        // true value means an unchecked box here always means the Edit icon really is hidden, and
        // checking it here is what actually makes the Edit icon appear.
        const handleEditableToggle = e => {
            const info = JSON.parse(e.target.dataset.info);
            updateMockData('injectDeletable', {
                ...info,
                deletable: e.target.checked
            });
        };

        const getEditableIndicator = (info, deletable) => {
            return (
                <label
                    title = 'Reflects/toggles the actual `deletable` flag stored on this document (drives the Edit icon in the main list)'
                    style = {{ ...inlineStyles.controlLabel }}
                >
                    <input
                        type = 'checkbox'
                        style = {{ ...inlineStyles.controlCheckbox }}
                        checked = { !!deletable }
                        onChange = { handleEditableToggle }
                        data-info = { JSON.stringify(info) }
                    />
                    <span>Editable</span>
                </label>
            );
        };

        const superUserButtonsConfig = [
            {
                buttonText: 'Verify',
                documentStatus: UPLOADED_DOCUMENT_STATUSES.VERIFIED,

            },
            {
                buttonText: 'Reject',
                documentStatus: UPLOADED_DOCUMENT_STATUSES.REJECTED,

            }
        ];

        const getSuperUserButtons = info => {
            return superUserButtonsConfig.map(b => {
                const { buttonText, documentStatus } = b;
                return (
                    <button
                        key = { buttonText }
                        style = {{ ...inlineStyles.button }}
                        onClick = { handleSuperUserAction }
                        data-info = { JSON.stringify({ ...info, documentStatus }) }
                    >
                        { buttonText }
                    </button>
                );
            });
        };

        // @ts-ignore
        return fullData.applicants.map(a => {

            const { applicantGuid, documents, name } = a;

            if(currentApplicantGuid !== applicantGuid) {
                return null;
            }

            return (
                <div
                    key = { applicantGuid }
                    style = {{
                        ...inlineStyles.extraWide,
                        border: '1px solid grey',
                        borderRadius: '0.5rem',
                        margin: '1rem',
                        padding: '1rem'
                    }}
                >
                    <div><b>{ normalizeNameOfPerson(name) }</b></div>
                    <br/>
                    <div>
                        {
                            documents.map(c => {
                                const { categoryType, documentTypes } = c;

                                return (
                                    <div key = { categoryType }>
                                        <div
                                            //@ts-ignore
                                            style = {{ ...inlineStyles.label, color: 'orange' }}
                                        >
                                            { categoryType }
                                        </div>
                                        {
                                            documentTypes.map(dt => {
                                                const { uploadedFileName, documentType, documentStatus, deletable } = dt;

                                                const info = {
                                                    applicantGuid,
                                                    categoryType,
                                                    documentType,
                                                    uploadedFileName
                                                };

                                                return (
                                                    <div
                                                        key = { uploadedFileName }
                                                        //@ts-ignore
                                                        style = {{ ...inlineStyles.documentRow }}
                                                    >
                                                        <span style = {{ ...inlineStyles.documentMeta }}>
                                                            <span
                                                                //@ts-ignore
                                                                style = {{ ...inlineStyles.label }}
                                                            >
                                                                { documentType }
                                                            </span>
                                                            <span
                                                                //@ts-ignore
                                                                style = {{ ...inlineStyles.label, color: 'green' }}
                                                            >
                                                                { uploadedFileName }
                                                            </span>
                                                            <span style = {{ ...inlineStyles.statusBadge }}>
                                                                ({ documentStatus })
                                                            </span>
                                                        </span>
                                                        <span style = {{ ...inlineStyles.documentControls }}>
                                                            {
                                                                documentStatus === UPLOADED_DOCUMENT_STATUSES.SUBMITTED &&
                                                                getSuperUserButtons(info)
                                                            }
                                                            { getVerifiedCheckbox(info, documentStatus) }
                                                            { getEditableIndicator(info, deletable) }
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            );
        } );
    };

    return (
        <div className = 'tablet:grid-col-2 padding-2'>
            {
                isExtended &&

                <Fragment>
                    {
                        <Fragment>
                            <b style = {{ position: 'relative' }}>
                                <span>
                                    Validation {allDocumentsAreProvided
                                        ? <span style={{ color: 'green' }}>Completed</span> :
                                        <span style={{ color: 'red' }}>Incomplete</span>
                                    }
                                </span>
                                <span
                                    style={{
                                        position: 'absolute',
                                        right: '-530px',
                                        top: '10px',
                                        color: 'darkred'
                                    }}
                                >
                                    In debug mode always upload documents with different names!
                                </span>
                            </b>
                            <br/>
                            <br/>
                        </Fragment>
                    }
                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'User switching',
                                null,
                                getUserSwitchButtons()
                            )
                        }
                    </div>

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'JSON Injector',
                                'isJsonInjectorShown',
                                <button
                                    style = {{
                                        ...inlineStyles.button
                                    }}
                                    key = { 'button' }
                                    name = { 'jsonInjector' }
                                    onClick = { handleJsonUpload }
                                >
                                    Upload Mock
                                </button>
                            )
                        }
                    </div>


                    {
                        isJsonInjectorShown &&

                            <div
                                style = {{
                                    ...inlineStyles.commonForSections,
                                    ...inlineStyles.spaceBetweenItems
                                }}
                            >
                                <textarea
                                    style={{
                                        width: '100%'
                                    }}
                                    rows = { 10 }
                                    placeholder = 'Paste Mock JSON here...'
                                    value = { json }
                                    onChange = { handleJsonChange }
                                />
                            </div>
                    }

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'File info:',
                                'isUploadViewShown'
                            )
                        }
                    </div>

                    {
                        isUploadViewShown &&

                        <div style = { inlineStyles.spaceBetweenItems } >
                            <pre
                                style = {{
                                    ...inlineStyles.commonForSections,
                                    ...inlineStyles.uploadSection
                                }}
                            >
                                { stringify(fileInfo) }
                            </pre>
                        </div>
                    }

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'UI Configuration:',
                                'isUiConfigViewShown',
                                getUiConfigButtons()
                            )
                        }
                    </div>

                    {
                        isUiConfigViewShown &&

                        <div style = { inlineStyles.spaceBetweenItems } >
                            <pre
                                style = {{
                                    ...inlineStyles.commonForSections,
                                    ...inlineStyles.uiConfigSection
                                }}
                            >
                                { getUiConfigDataView() }
                            </pre>
                        </div>
                    }

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'Data received from BE:',
                                'isBackEndDataShown',
                                getDataButtons()
                            )
                        }
                    </div>

                    {
                        isBackEndDataShown &&

                        <div style = { inlineStyles.spaceBetweenItems } >
                            <pre
                                style = {{
                                    ...inlineStyles.commonForSections,
                                    ...inlineStyles.backEndDataSection
                                }}
                            >
                                { getBackEndDataView() }
                            </pre>
                        </div>

                    }

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            getSectionHeader(
                                'Process Documents:',
                                'isProcessingShown'
                            )
                        }
                    </div>

                    <div style = { inlineStyles.spaceBetweenItems } >
                        {
                            isProcessingShown &&

                                getApprovalRejectionDashboard()

                        }
                    </div>

                </Fragment>
            }

        </div>
    );

};


export default UploadHyperDashboard;